import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ModeProvider } from "./contexts/ModeContext";
import { AuthCallback, ProtectedRoute } from "./components/AuthCallback";
import ErrorBoundary from "./components/ErrorBoundary";
import PageErrorBoundary from "./components/PageErrorBoundary";
import { LoginPage } from "./pages/LoginPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { RegisterPage } from "./pages/RegisterPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ChantiersPage } from "./pages/ChantiersPage";
import { ReservesPage } from "./pages/ReservesPage";
import { RapportsPage } from "./pages/RapportsPage";
import { SecuritePage } from "./pages/SecuritePage";
import { ProfilPage } from "./pages/ProfilPage";
import { PlanningPage } from "./pages/PlanningPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ChantiersCSPSPage } from "./pages/ChantiersCSPSPage";
import { VisitesCSPSPage } from "./pages/VisitesCSPSPage";
import { InspectionsPage } from "./pages/InspectionsPage";
import { DocumentsCSPSPage } from "./pages/DocumentsCSPSPage";
import { RapportsCSPSPage } from "./pages/RapportsCSPSPage";
import { PresentationPage } from "./pages/PresentationPage";
import VitrinePage from "./pages/VitrinePage";
import { SlideshowPage } from "./pages/SlideshowPage";
import { SubscriptionPage, SubscriptionSuccessPage } from "./pages/SubscriptionPage";
import { AdminPlansPage } from "./pages/AdminPlansPage";
import { AdminNotificationsPage } from "./pages/AdminNotificationsPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AdminPilotsPage } from "./pages/AdminPilotsPage";
import { BorisChantierPage } from "./pages/BorisChantierPage";
import { MeetingReportsPage } from "./pages/MeetingReportsPage";
import { SiteVisitsPage } from "./pages/SiteVisitsPage";
import VisitesGlobalPage from "./pages/VisitesGlobalPage";
import { DocumentsOPCPage } from "./pages/DocumentsOPCPage";
import { AnnuairePage } from "./pages/AnnuairePage";
import { CGVPage, MentionsLegalesPage, ConfidentialitePage, CGUPage } from "./pages/LegalPages";
import { ChantierJournalPage } from "./pages/ChantierJournalPage";
import { JournalHubPage } from "./pages/JournalHubPage";
import EntrepriseVuePage from "./pages/EntrepriseVuePage";
import ChantixAIPage from "./pages/ChantixAIPage";
import { Toaster } from "./components/ui/sonner";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (from Google OAuth callback)
  // This must be synchronous during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/cgv" element={<CGVPage />} />
      <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
      <Route path="/confidentialite" element={<ConfidentialitePage />} />
      <Route path="/cgu" element={<CGUPage />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <PageErrorBoundary><DashboardPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers" element={
        <ProtectedRoute>
          <PageErrorBoundary><ChantiersPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers/nouveau" element={
        <ProtectedRoute>
          <PageErrorBoundary><ChantiersPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers/:id" element={
        <ProtectedRoute>
          <PageErrorBoundary><ChantiersPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers/:id/journal" element={
        <ProtectedRoute>
          <PageErrorBoundary><ChantierJournalPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/journal" element={
        <ProtectedRoute>
          <PageErrorBoundary><JournalHubPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/reserves" element={
        <ProtectedRoute>
          <PageErrorBoundary><ReservesPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/reserves/nouvelle" element={
        <ProtectedRoute>
          <PageErrorBoundary><ReservesPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/planning" element={
        <ProtectedRoute>
          <PageErrorBoundary><PlanningPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/rapports" element={<Navigate to="/documents-opc" replace />} />
      <Route path="/documents-opc" element={
        <ProtectedRoute>
          <PageErrorBoundary><DocumentsOPCPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/rapports/generer" element={<Navigate to="/documents-opc" replace />} />
      <Route path="/securite" element={
        <ProtectedRoute>
          <PageErrorBoundary><SecuritePage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/securite/nouvelle" element={
        <ProtectedRoute>
          <PageErrorBoundary><SecuritePage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/profil" element={
        <ProtectedRoute>
          <PageErrorBoundary><ProfilPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <PageErrorBoundary><SettingsPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      
      {/* CSPS routes */}
      <Route path="/csps/chantiers" element={
        <ProtectedRoute requireCSPS>
          <PageErrorBoundary><ChantiersCSPSPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/csps/visites" element={
        <ProtectedRoute requireCSPS>
          <PageErrorBoundary><VisitesCSPSPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/csps/inspections" element={
        <ProtectedRoute requireCSPS>
          <PageErrorBoundary><InspectionsPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/csps/documents" element={
        <ProtectedRoute requireCSPS>
          <PageErrorBoundary><DocumentsCSPSPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/csps/rapports" element={<Navigate to="/csps/documents" replace />} />
      <Route path="/admin/presentation" element={
        <ProtectedRoute>
          <PageErrorBoundary><PresentationPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/presentation" element={
        <PageErrorBoundary><VitrinePage /></PageErrorBoundary>
      } />
      <Route path="/slideshow" element={
        <ProtectedRoute>
          <PageErrorBoundary><SlideshowPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <PageErrorBoundary><SubscriptionPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/abonnement" element={<Navigate to="/subscription" replace />} />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <PageErrorBoundary><SubscriptionSuccessPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin/plans" element={
        <ProtectedRoute>
          <PageErrorBoundary><AdminPlansPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin/notifications" element={
        <ProtectedRoute>
          <PageErrorBoundary><AdminNotificationsPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <PageErrorBoundary><AdminUsersPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin/pilots" element={
        <ProtectedRoute>
          <PageErrorBoundary><AdminPilotsPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers-csps/:chantierId/boris" element={
        <ProtectedRoute requireCSPS>
          <PageErrorBoundary><BorisChantierPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers/:chantierId/boris" element={
        <ProtectedRoute>
          <PageErrorBoundary><BorisChantierPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers/:chantierId/meeting-reports" element={
        <ProtectedRoute>
          <PageErrorBoundary><MeetingReportsPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers/:chantierId/site-visits" element={
        <ProtectedRoute>
          <PageErrorBoundary><SiteVisitsPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/visites" element={
        <ProtectedRoute>
          <PageErrorBoundary><VisitesGlobalPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantiers/:chantierId/entreprises" element={
        <ProtectedRoute>
          <PageErrorBoundary><EntrepriseVuePage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/chantix-ai" element={
        <ProtectedRoute>
          <PageErrorBoundary><ChantixAIPage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/annuaire" element={
        <ProtectedRoute>
          <PageErrorBoundary><AnnuairePage /></PageErrorBoundary>
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <ModeProvider>
              <AppRouter />
              <Toaster position="top-center" />
            </ModeProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
